import { Button } from 'components/atoms';
import WarningPopup from 'components/molecules/popup/warning-popup/WarningPopup';
import { Form } from 'components/organisms';
import {
  labelInitialValues,
  LabelTabs,
} from 'components/organisms/forms/label/LabelForm';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import middleware from 'middleware';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiIdentificationCard, PiX } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import {
  usePostLabel,
  useValidateLabelAccount,
} from 'services/labels';
import {
  CUSTOMER_TYPE,
  ILabelForm,
  LABEL_STATUS,
} from 'services/labels/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

const CreateLabel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createLabel = usePostLabel();
  const validateAccount = useValidateLabelAccount();

  const mutateOptions = useMutateOptions<ILabelForm>();

  const [showValidationErrorPopup, setShowValidationErrorPopup] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<{
    values: ILabelForm;
    helpers: FormikHelpers<ILabelForm>;
  } | null>(null);

  const performSave = async (
    values: ILabelForm,
    helpers: FormikHelpers<ILabelForm>
  ) => {
    await createLabel.mutateAsync(middleware.label.formToQuery(values), {
      ...mutateOptions(helpers),
      onSuccess: ({ id }) => navigate(`/label/${id}/finances`),
    });
  };

  const onCreateLabel = async (
    values: ILabelForm,
    helpers: FormikHelpers<ILabelForm>
  ) => {
    if (values.labelStatus === LABEL_STATUS.Draft) {
      await performSave(values, helpers);
      return;
    }

    try {
      const countryAlfa2 =
        values.customerType === CUSTOMER_TYPE.Physical
          ? values.personalIdCountryAlfa2
          : values.companyCountryAlfa2;

      const validationResult = await validateAccount.mutateAsync({
        bankAccountNumber: values.bankAccountNumber,
        swift: values.swift,
        countryAlfa2: countryAlfa2,
        routingNumber: values.routingNumber || '',
      });

      if (validationResult === true) {
        await performSave(values, helpers);
      } else {
        setPendingSaveData({ values, helpers });
        setShowValidationErrorPopup(true);
      }
    } catch (error) {
      setPendingSaveData(null);
    }
  };

  const handleContinueSave = async () => {
    if (pendingSaveData) {
      setShowValidationErrorPopup(false);
      await performSave(pendingSaveData.values, pendingSaveData.helpers);
      setPendingSaveData(null);
    }
  };

  return (
    <>
      <Form.Label
        i18nextPrefix='label'
        initialValues={{
          ...labelInitialValues,
        }}
        onSubmit={onCreateLabel}
        customBackItem={
          <Button type='button' onlyIcon onClick={() => navigate('/label')}>
            <Button.Description description={t('button.cancel')} />

            <Button.Icon icon={<PiX />} />
          </Button>
        }
        topbarContent={
          <Button type='submit' variant={BUTTON_VARIANT.submit}>
            <Button.Text text={t('button.create')} />
            <Button.Icon icon={<PiIdentificationCard />} />
          </Button>
        }
        disableTabs={[
          LabelTabs.residenceCertificates,
          LabelTabs.contracts,
          LabelTabs.balance,
          LabelTabs.settlements,
          LabelTabs.reports,
          LabelTabs.advance,
          LabelTabs.statistics,
        ]}
      />
      <WarningPopup
        open={showValidationErrorPopup}
        onClose={() => {
          setShowValidationErrorPopup(false);
          setPendingSaveData(null);
        }}
        onConfirm={handleContinueSave}
        title={t('label.account.validation.warning.title')}
        message={t('label.account.validation.warning.message')}
        confirmButtonVariant={BUTTON_VARIANT.remove}
      />
    </>
  );
};

export default CreateLabel;
